import { useEffect, useMemo, useRef } from 'react';
import { useInfiniteQuery, type InfiniteData } from '@tanstack/react-query';

import { PER_PAGE, fetchScoresPage, type LeaderboardError } from './leaderboard.api';
import type {
  LeaderboardCursor,
  LeaderboardFilter,
  LeaderboardPage,
  LeaderboardRowData,
} from './types';

/** Stable reference so an empty board doesn't re-trigger framer-motion layout. */
const EMPTY: LeaderboardPage = [];

type LeaderboardQueryKey = readonly [
  'quiz_leaderboard',
  string,
  string | undefined,
  LeaderboardFilter,
  number | undefined,
];

/**
 * Dedupes scorers by id (keeping the higher score), sorts by `totalScore DESC, name ASC`,
 * and recomputes each bar's width against the global max. The server sends a
 * per-page `relativePercentage`, which is meaningless once pages are concatenated.
 */
export const mergeLeaders = (rows: LeaderboardRowData[]): LeaderboardRowData[] => {
  const byId = new Map<string, LeaderboardRowData>();
  rows.forEach((row) => {
    const current = byId.get(row.id);
    if (!current || row.totalScore >= current.totalScore) byId.set(row.id, row);
  });

  // `Array.from` rather than spread — the ui tsconfig targets es5.
  const sorted = Array.from(byId.values()).sort((a, b) => (
    b.totalScore !== a.totalScore
      ? b.totalScore - a.totalScore
      : (a.commenterName || '').localeCompare(b.commenterName || '')
  ));

  const globalMax = sorted[0]?.totalScore || 0;
  return sorted.map((leader) => ({
    ...leader,
    relativePercentage: globalMax > 0 ? Math.round((leader.totalScore / globalMax) * 100) : 0,
  }));
};

interface UseLeaderboardArgs {
  streamId?: string;
  featureId?: string;
  filter: LeaderboardFilter;
  questionNumber?: number;
  enabled?: boolean;
}

export const useLeaderboard = ({
  streamId,
  featureId,
  filter,
  questionNumber,
  enabled = true,
}: UseLeaderboardArgs) => {
  const isAllFilter = filter === 'all';
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const query = useInfiniteQuery<
    LeaderboardPage,
    LeaderboardError,
    InfiniteData<LeaderboardPage, LeaderboardCursor | null>,
    LeaderboardQueryKey,
    LeaderboardCursor | null
  >({
    // `featureId` and `questionNumber` in the key are what reset the board when the
    // presenter moves on — a different key is a different cache entry.
    queryKey: ['quiz_leaderboard', streamId ?? '', featureId, filter, questionNumber],
    // Non-null is safe: the query is gated behind `enabled` below.
    queryFn: ({ pageParam }) => fetchScoresPage({
      streamId: streamId!, filter, questionNumber, cursor: pageParam,
    }),
    initialPageParam: null,
    getNextPageParam: (lastPage) => {
      if (!isAllFilter) return undefined;
      if (lastPage.length < PER_PAGE) return undefined;
      const tail = lastPage[lastPage.length - 1];
      return { afterScore: tail.totalScore, afterName: tail.commenterName };
    },
    // Deliberately no `refetchInterval` — unlike Fantastic Fans, the board is not
    // polled. It refreshes on mount, on window focus, and whenever the key below
    // changes (new question, changed winner filter), which is what shipped before.
    // This endpoint aggregates every quiz_score row per call, so polling it is costly.
    refetchOnWindowFocus: true,
    // Hard reset on feature change — don't restore a previously scrolled board.
    gcTime: 0,
    enabled: enabled && Boolean(streamId) && Boolean(featureId),
  });

  const { data, hasNextPage } = query;

  const leaders = useMemo(() => {
    const pages = data?.pages;
    if (!pages?.length) return EMPTY;
    // Numeric filters are a single unpaginated top-N page: keep the server's
    // ordering and its percentages verbatim.
    return isAllFilter ? mergeLeaders(pages.flat()) : pages[0];
  }, [data, isAllFilter]);

  // The observer closure only re-runs on `hasNextPage`, so read the live query
  // through a ref rather than capturing stale flags.
  const queryRef = useRef(query);
  queryRef.current = query;

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasNextPage) return undefined;

    // An IntersectionObserver works regardless of which ancestor actually
    // scrolls — unlike a scroll listener bound to a specific element.
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0]?.isIntersecting) return;
      const current = queryRef.current;
      if (!current.hasNextPage || current.isFetching || current.isFetchingNextPage) return;
      // `cancelRefetch` defaults to true, which would let a re-firing observer
      // cancel and restart the page it just asked for.
      void current.fetchNextPage({ cancelRefetch: false });
    }, { rootMargin: '200px' });

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage]);

  return {
    leaders,
    sentinelRef,
    // `isLoading` rather than `isPending`: the latter stays true while the query
    // is disabled, which would pin the loader.
    isLoading: query.isLoading,
    hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
  } as const;
};
